import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient, useQuery } from 'react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  ArrowLeft,
  Users,
  Building2,
  Mail,
  User,
  Phone,
  MapPin,
  CreditCard,
  Eye,
  EyeOff,
  Save,
  Palette,
  Briefcase
} from 'lucide-react'
import { INDUSTRY_OPTIONS } from '../../utils/industrySchemas'
import { motion } from 'framer-motion'
import LoadingSpinner from '../common/LoadingSpinner'

const NewClient = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showPassword, setShowPassword] = useState(false)

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: { subscription_plan: 'basic', edit_quota: 50 }
  })

  const { data: billingPlans, isLoading: plansLoading } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(res => res.data)
  )

  const subscriptionPlan = watch('subscription_plan')

  const createClientMutation = useMutation(
    (clientData) => apiService.post('/clients', clientData),
    {
      onSuccess: () => {
        toast.success('Client created successfully!')
        queryClient.invalidateQueries(['clients'])
        navigate('/admin/clients')
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to create client')
    }
  )

  const onSubmit = (data) => {
    createClientMutation.mutate({
      name: data.name,
      email: data.email,
      password: data.password,
      company_name: data.company_name,
      subscription_plan: data.subscription_plan,
      edit_quota: parseInt(data.edit_quota),
      brand_colors: data.brand_colors ? [data.brand_colors] : [],
      industry: data.industry || 'general',
    })
  }

  const planOptions = billingPlans ? Object.entries(billingPlans).map(([id, plan]) => ({
    id,
    name: plan.name,
    description: `£${plan.price}/month${plan.custom ? ' · Custom' : ''}`,
    quota: plan.features.editsPerMonth,
    price: plan.price,
    type: plan.custom ? 'custom' : 'standard',
    features: [
      `${plan.features.sites === 'unlimited' ? 'Unlimited' : plan.features.sites} sites`,
      `${plan.features.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features.editsPerMonth} edits/month`,
      plan.features.storage,
      plan.features.support
    ]
  })) : []

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*'
    let password = ''
    for (let i = 0; i < 12; i++) password += chars.charAt(Math.floor(Math.random() * chars.length))
    return password
  }

  return (
    <div style={{ paddingTop: 16 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ display: 'flex', flexDirection: 'column', gap: 32 }}
      >
        <div className="flex items-center gap-4">
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => navigate('/admin/clients')}>
            <ArrowLeft style={{ width: 20, height: 20 }} />
          </button>
          <div>
            <h1 className="h2">Add New Client</h1>
            <p className="text-muted" style={{ fontSize: 13 }}>Create a new client account and set up their subscription</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 32 }}>
          {/* Form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* User Information */}
              <div className="card card-pad">
                <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                  <div style={{ padding: 8, background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
                    <User style={{ width: 20, height: 20, color: 'var(--accent)' }} />
                  </div>
                  <div>
                    <p className="font-semibold">User Information</p>
                    <p className="text-muted" style={{ fontSize: 12 }}>Basic user account information for the client</p>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                      <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                        Full Name <span style={{ color: 'var(--danger)' }}>*</span>
                      </label>
                      <input
                        type="text"
                        {...register('name', { required: 'Name is required' })}
                        className="input"
                        placeholder="John Doe"
                        style={errors.name ? { borderColor: 'var(--danger)' } : {}}
                      />
                      {errors.name && <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{errors.name.message}</p>}
                    </div>
                    <div>
                      <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                        Email Address <span style={{ color: 'var(--danger)' }}>*</span>
                      </label>
                      <input
                        type="email"
                        {...register('email', {
                          required: 'Email is required',
                          pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' }
                        })}
                        className="input"
                        placeholder="john@company.com"
                        style={errors.email ? { borderColor: 'var(--danger)' } : {}}
                      />
                      {errors.email && <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{errors.email.message}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                      Password <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        {...register('password', {
                          required: 'Password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters' }
                        })}
                        className="input"
                        placeholder="Enter password"
                        style={{ paddingRight: 96, ...(errors.password ? { borderColor: 'var(--danger)' } : {}) }}
                      />
                      <div style={{ position: 'absolute', inset: '0 0 0 auto', display: 'flex', alignItems: 'center', paddingRight: 8, gap: 4 }}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize: 11, color: 'var(--accent)', padding: '2px 6px', height: 'auto' }}
                          onClick={() => setValue('password', generatePassword(), { shouldValidate: true })}
                        >
                          Generate
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />}
                        </button>
                      </div>
                    </div>
                    {errors.password && <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{errors.password.message}</p>}
                  </div>
                </div>
              </div>

              {/* Company Information */}
              <div className="card card-pad">
                <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                  <div style={{ padding: 8, background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
                    <Building2 style={{ width: 20, height: 20, color: 'var(--accent)' }} />
                  </div>
                  <div>
                    <p className="font-semibold">Company Information</p>
                    <p className="text-muted" style={{ fontSize: 12 }}>Business details and branding preferences</p>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                      Company Name <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    <input
                      type="text"
                      {...register('company_name', { required: 'Company name is required' })}
                      className="input"
                      placeholder="Acme Corporation"
                      style={errors.company_name ? { borderColor: 'var(--danger)' } : {}}
                    />
                    {errors.company_name && <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{errors.company_name.message}</p>}
                  </div>
                  <div>
                    <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                      Industry <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    <select
                      {...register('industry', { required: 'Industry is required' })}
                      className="input"
                      style={errors.industry ? { borderColor: 'var(--danger)' } : {}}
                    >
                      <option value="">Select an industry...</option>
                      {INDUSTRY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
                      ))}
                    </select>
                    {errors.industry && <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{errors.industry.message}</p>}
                  </div>
                  <div>
                    <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Brand Color</label>
                    <input
                      type="color"
                      {...register('brand_colors')}
                      style={{ width: '100%', height: 40, border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', padding: 4 }}
                    />
                  </div>
                </div>
              </div>

              {/* Subscription Plan */}
              <div className="card card-pad">
                <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                  <div style={{ padding: 8, background: 'var(--success-soft)', borderRadius: 'var(--radius)' }}>
                    <CreditCard style={{ width: 20, height: 20, color: 'var(--success)' }} />
                  </div>
                  <div>
                    <p className="font-semibold">Subscription Plan</p>
                    <p className="text-muted" style={{ fontSize: 12 }}>Choose the appropriate subscription plan for the client</p>
                  </div>
                </div>
                {plansLoading ? (
                  <div className="flex items-center justify-center" style={{ padding: '32px 0' }}>
                    <LoadingSpinner size="sm" text="Loading plans..." />
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                      {planOptions.map((plan) => (
                        <label
                          key={plan.id}
                          style={{
                            position: 'relative', cursor: 'pointer', borderRadius: 'var(--radius)',
                            border: subscriptionPlan === plan.id ? '2px solid var(--accent)' : '1px solid var(--border)',
                            padding: 16, transition: 'all 0.2s',
                            background: subscriptionPlan === plan.id ? 'var(--accent-soft)' : 'var(--surface)'
                          }}
                        >
                          <input type="radio" value={plan.id} {...register('subscription_plan')} style={{ position: 'absolute', opacity: 0 }} />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <p className="font-medium">{plan.name}</p>
                            <p className="text-muted" style={{ fontSize: 12 }}>{plan.description}</p>
                            <p className="font-medium" style={{ fontSize: 13, color: 'var(--accent)' }}>£{plan.price}/month</p>
                            <p style={{ fontSize: 11, color: 'var(--text-soft)' }}>{plan.quota === 'unlimited' ? 'Unlimited' : plan.quota} edits</p>
                            <ul style={{ fontSize: 11, color: 'var(--text-muted)', paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {plan.features.map((feature, index) => (
                                <li key={index}>• {feature}</li>
                              ))}
                            </ul>
                          </div>
                        </label>
                      ))}
                    </div>
                    <div>
                      <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Monthly Edit Quota</label>
                      <input
                        type="number"
                        min="1"
                        max="1000"
                        {...register('edit_quota', { required: 'Edit quota is required', min: { value: 1, message: 'Minimum 1 edit' } })}
                        className="input"
                        placeholder="50"
                        style={errors.edit_quota ? { borderColor: 'var(--danger)' } : {}}
                      />
                      {errors.edit_quota && <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{errors.edit_quota.message}</p>}
                      <p className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>Override the default plan quota if needed</p>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, position: 'sticky', top: 16, alignSelf: 'start' }}>
            <div className="card card-pad">
              <p className="font-semibold" style={{ marginBottom: 4 }}>Client Preview</p>
              <p className="text-muted" style={{ fontSize: 12, marginBottom: 16 }}>Review the client configuration before creating</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
                <div className="flex justify-between">
                  <span className="text-muted">Plan:</span>
                  <span className="font-medium">{planOptions.find(p => p.id === subscriptionPlan)?.name || 'Basic'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Plan Price:</span>
                  <span className="font-medium">£{planOptions.find(p => p.id === subscriptionPlan)?.price || 0}/month</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Edit Quota:</span>
                  <span className="font-medium">{planOptions.find(p => p.id === subscriptionPlan)?.quota || 50}/month</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Status:</span>
                  <span className="badge badge-warning">Pending payment</span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                className="btn btn-primary w-full"
                onClick={handleSubmit(onSubmit)}
                disabled={createClientMutation.isLoading}
                style={{ height: 44 }}
              >
                {createClientMutation.isLoading ? <LoadingSpinner size="sm" /> : (
                  <><Save style={{ width: 16, height: 16, marginRight: 8 }} />Create Client</>
                )}
              </button>
              <button
                className="btn w-full"
                onClick={() => navigate('/admin/clients')}
                style={{ height: 44 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default NewClient
